import { ResponseArtifactDTO } from '@dreamai/types';

/**
 * Wire shape of the `expense_review` response artifact. Mirrors
 * `ExpenseReview*` in `@dreamai/types` (api/response-artifacts.ts); import
 * from the package instead once the version carrying them is installed.
 */
export const EXPENSE_REVIEW_ARTIFACT_TYPE = 'expense_review';

export type ExpenseReviewItemStatus =
  | 'draft'
  | 'awaiting_info'
  | 'awaiting_confirmation'
  | 'approved'
  | 'submitted'
  | 'failed'
  | 'discarded';

export type ExpensePaymentMethod = 'company_card' | 'personal_card';

export interface ExpenseReviewReceipt {
  /** `/api/files/<id>` reference to the receipt or invoice in Dream storage. */
  fileRef: string;
  filename: string | null;
  contentType: string | null;
}

export interface ExpenseReviewItem {
  expenseId: string;
  status: ExpenseReviewItemStatus;
  vendor: string | null;
  /** ISO date (YYYY-MM-DD). */
  transactionDate: string | null;
  amount: number | null;
  currency: string | null;
  settledAmount: number | null;
  settledCurrency: string | null;
  description: string | null;
  categoryAccountCode: string | null;
  categoryName: string | null;
  taxType: string | null;
  paymentMethod: ExpensePaymentMethod | null;
  bankAccountName: string | null;
  duplicateOfExpenseId: string | null;
  receipts: ExpenseReviewReceipt[];
  providerUrl: string | null;
}

export interface ExpenseReviewUserResponse {
  action: 'submit';
  approved: true;
  /** Omitted = every expense on the review. */
  expenseIds?: string[];
  via: 'web';
}

const ITEM_STATUSES: ReadonlySet<string> = new Set<ExpenseReviewItemStatus>([
  'draft',
  'awaiting_info',
  'awaiting_confirmation',
  'approved',
  'submitted',
  'failed',
  'discarded',
]);

const PAYMENT_METHODS: ReadonlySet<string> = new Set<ExpensePaymentMethod>([
  'company_card',
  'personal_card',
]);

export function isExpenseReviewArtifact(
  artifact: Pick<ResponseArtifactDTO, 'artifactType'> | null | undefined,
): boolean {
  return (
    (artifact?.artifactType as string | undefined) ===
    EXPENSE_REVIEW_ARTIFACT_TYPE
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function str(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function num(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function parseReceipt(raw: unknown): ExpenseReviewReceipt | null {
  if (!isRecord(raw)) return null;
  const fileRef = str(raw.fileRef);
  if (!fileRef) return null;
  return {
    fileRef,
    filename: str(raw.filename),
    contentType: str(raw.contentType),
  };
}

function parseItem(raw: unknown): ExpenseReviewItem | null {
  if (!isRecord(raw)) return null;
  const expenseId = str(raw.expenseId);
  if (!expenseId) return null;
  const status = str(raw.status);
  const paymentMethod = str(raw.paymentMethod);
  const receipts = Array.isArray(raw.receipts)
    ? raw.receipts
        .map(parseReceipt)
        .filter((r): r is ExpenseReviewReceipt => r !== null)
    : [];
  return {
    expenseId,
    status:
      status && ITEM_STATUSES.has(status)
        ? (status as ExpenseReviewItemStatus)
        : 'awaiting_confirmation',
    vendor: str(raw.vendor),
    transactionDate: str(raw.transactionDate),
    amount: num(raw.amount),
    currency: str(raw.currency),
    settledAmount: num(raw.settledAmount),
    settledCurrency: str(raw.settledCurrency),
    description: str(raw.description),
    categoryAccountCode: str(raw.categoryAccountCode),
    categoryName: str(raw.categoryName),
    taxType: str(raw.taxType),
    paymentMethod:
      paymentMethod && PAYMENT_METHODS.has(paymentMethod)
        ? (paymentMethod as ExpensePaymentMethod)
        : null,
    bankAccountName: str(raw.bankAccountName),
    duplicateOfExpenseId: str(raw.duplicateOfExpenseId),
    receipts,
    providerUrl: str(raw.providerUrl),
  };
}

/**
 * Reads the review items off an artifact DTO. The server maps the stored
 * JSON to `expenseReview`; `fields` is accepted as well so an older server
 * that still returns the raw column renders too. Malformed rows are dropped.
 */
export function readExpenseReviewItems(
  artifact: ResponseArtifactDTO,
): ExpenseReviewItem[] {
  const dto: Record<string, unknown> = artifact as unknown as Record<
    string,
    unknown
  >;
  let raw: unknown = dto.expenseReview ?? dto.fields;
  if (typeof raw === 'string') {
    try {
      raw = JSON.parse(raw);
    } catch {
      return [];
    }
  }
  if (!isRecord(raw) || !Array.isArray(raw.items)) return [];
  return raw.items
    .map(parseItem)
    .filter((item): item is ExpenseReviewItem => item !== null);
}

export function buildExpenseReviewSubmitResponse(
  selected: string[],
  all: ExpenseReviewItem[],
): ExpenseReviewUserResponse {
  const everything =
    selected.length === all.length &&
    all.every((item) => selected.includes(item.expenseId));
  return {
    action: 'submit',
    approved: true,
    ...(everything ? {} : { expenseIds: selected }),
    via: 'web',
  };
}

export function formatExpenseMoney(
  amount: number | null,
  currency: string | null,
  locale?: string,
): string | null {
  if (amount === null) return null;
  if (!currency) return amount.toFixed(2);
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${currency} ${amount.toFixed(2)}`;
  }
}

export function formatExpenseDate(
  iso: string | null,
  locale?: string,
): string | null {
  if (!iso) return null;
  const date = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return iso;
  return new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(date);
}

/** i18n key under `expenseReview.paymentMethod`, or null when unknown. */
export function expensePaymentMethodKey(
  method: ExpensePaymentMethod | null,
): string | null {
  switch (method) {
    case 'company_card':
    case 'personal_card':
      return `expenseReview.paymentMethod.${method}`;
    default:
      return null;
  }
}

const STATUS_KEYS: ReadonlySet<string> = new Set([
  'draft',
  'awaiting_info',
  'awaiting_confirmation',
  'approved',
  'submitted',
  'failed',
  'discarded',
]);

/** i18n key under `expenseReview.status`; unknown statuses render as Draft. */
export function expenseStatusKey(status: ExpenseReviewItemStatus): string {
  return `expenseReview.status.${STATUS_KEYS.has(status) ? status : 'draft'}`;
}

export function isExpenseSubmittable(item: ExpenseReviewItem): boolean {
  return item.status === 'awaiting_confirmation' || item.status === 'approved';
}
